import { lazy, Suspense, useState } from 'react';
import { IconButton } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useJsonForms } from '@jsonforms/react';
import { useTranslation } from 'react-i18next';
import { toContentFormat } from '@/lib/formatContent';

const FormattedContentPanelLazy = lazy(() => import('@/components/FormattedContentPanel'));

/** The uischema option the generator emits from the DSL's field-level `ui.contentFormat`. */
export type ContentFormatOption = 'xml' | 'json' | { fromField: string };

interface ContentViewAdornmentProps {
  option: ContentFormatOption;
  title: string;
  value: string;
  readOnly: boolean;
  onChange: (value: string) => void;
}

/**
 * The view icon at the end of a text control whose field holds a document, and the side panel
 * it opens. The format is either declared on the field or read, per record, from the sibling
 * field the declaration names (a staging record's rawMessageFormat).
 *
 * Hidden until the control is hovered or the button has keyboard focus — the host control's sx
 * reveals `.nexo-content-view`, as LookupRenderer reveals its quick-view icon.
 */
export function ContentViewAdornment({ option, title, value, readOnly, onChange }: ContentViewAdornmentProps) {
  const { t } = useTranslation();
  const ctx = useJsonForms();
  const [open, setOpen] = useState(false);
  // Mounted on first open and kept, so closing slides the panel out instead of unmounting it.
  const [mounted, setMounted] = useState(false);
  const formData = (ctx.core?.data ?? {}) as Record<string, unknown>;
  const format = toContentFormat(typeof option === 'string' ? option : formData[option.fromField]);
  const label = t('formattedContent.view', { defaultValue: 'View formatted' });

  return (
    <>
      <IconButton
        className="nexo-content-view"
        size="small"
        title={label}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          setMounted(true);
          setOpen(true);
        }}
        sx={{ opacity: open ? 1 : 0, transition: 'opacity 120ms', p: 0.25, alignSelf: 'flex-start' }}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
      {mounted && (
        <Suspense fallback={null}>
          <FormattedContentPanelLazy
            open={open}
            onClose={() => setOpen(false)}
            title={title}
            value={value}
            format={format}
            readOnly={readOnly}
            onChange={onChange}
          />
        </Suspense>
      )}
    </>
  );
}
